import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import cx from "classnames";
import { ReactNode, RefObject, useCallback, useLayoutEffect, useMemo, useState } from "react";

import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import { calculateVirtualPadding } from "ds/components/Table/hooks/useStickyRow/helpers";
import useStickyRangeExtractor from "ds/components/Table/hooks/useStickyRow/useStickyRangeExtractor";
import TableBodyRow from "ds/components/Table/Row";
import { OVERSCAN, ROW_HEIGHT_ESTIMATE } from "ds/components/TableSimple/constants";

import { flattenExpandableStickyRows } from "./helpers";
import SpaceDetailsExpandableStickyRow from "./StickyRow";
import styles from "./styles.module.css";

export type ExpandableStickyRowsContext = {
  isExpandable: boolean;
  isExpanded: boolean;
  onToggle: () => void;
};

type SpaceDetailsExpandableStickyRowsProps<TParent, TChild> = {
  data: TParent[];
  tableRef: RefObject<HTMLDivElement | null>;
  getParentId: (parent: TParent) => string;
  getChildren: (parent: TParent) => TChild[];
  getChildId: (child: TChild) => string;
  renderParentCells: (parent: TParent, context: ExpandableStickyRowsContext) => ReactNode;
  renderChildCells: (child: TChild, parent: TParent) => ReactNode;
};

const SpaceDetailsExpandableStickyRows = <TParent, TChild>({
  data,
  tableRef,
  getParentId,
  getChildren,
  getChildId,
  renderParentCells,
  renderChildCells,
}: SpaceDetailsExpandableStickyRowsProps<TParent, TChild>) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const toggleRow = useCallback((itemId: string) => {
    setExpandedRows((previous) => {
      const next = new Set(previous);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  const { rows, stickyRanges } = useMemo(
    () => flattenExpandableStickyRows(data, expandedRows, getParentId, getChildren),
    [data, expandedRows, getParentId, getChildren]
  );

  const { rangeExtractor, stickyGroupsVisibleOnTableRef } = useStickyRangeExtractor(stickyRanges);

  const getItemKey = useCallback(
    (index: number) => {
      const row = rows[index];
      return row.kind === "parent"
        ? getParentId(row.parent)
        : `${getParentId(row.parent)}-${getChildId(row.child)}`;
    },
    [rows, getParentId, getChildId]
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: OVERSCAN,
    getItemKey,
    rangeExtractor,
  });

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [rows.length, virtualizer]);

  const virtualizedItems = virtualizer.getVirtualItems();
  const [topGap, bottomGap] = calculateVirtualPadding(
    virtualizedItems,
    virtualizer.getTotalSize(),
    virtualizer.options.scrollMargin,
    stickyGroupsVisibleOnTableRef.current.size
  );

  const renderRow = (virtualRow: VirtualItem) => {
    const row = rows[virtualRow.index];
    const isLast = virtualRow.index === rows.length - 1;

    if (row.kind === "child") {
      return (
        <TableBodyRow
          key={virtualRow.key}
          {...keyboardProps}
          ref={virtualizer.measureElement}
          dataIndex={virtualRow.index}
          className={cx(styles.childRow, { [styles.lastRow]: isLast })}
          virtualized
        >
          {renderChildCells(row.child, row.parent)}
        </TableBodyRow>
      );
    }

    const parentCells = renderParentCells(row.parent, {
      isExpandable: row.isExpandable,
      isExpanded: row.isExpanded,
      onToggle: () => toggleRow(getParentId(row.parent)),
    });

    if (row.isExpanded) {
      const stickyGroupEnd = stickyGroupsVisibleOnTableRef.current.get(virtualRow.index);
      const isActiveSticky = stickyGroupEnd !== undefined;
      const nextAfter = isActiveSticky
        ? virtualizer.measurementsCache[stickyGroupEnd + 1]
        : undefined;
      const nextAfterSticky = nextAfter
        ? nextAfter.start - (virtualizer.measurementsCache[virtualRow.index]?.size ?? 0) + 1
        : undefined;

      return (
        <SpaceDetailsExpandableStickyRow
          key={virtualRow.key}
          keyboardProps={keyboardProps}
          measureRef={virtualizer.measureElement}
          dataIndex={virtualRow.index}
          isActiveSticky={isActiveSticky}
          nextAfterSticky={nextAfterSticky}
          tableRef={tableRef}
        >
          {parentCells}
        </SpaceDetailsExpandableStickyRow>
      );
    }

    return (
      <TableBodyRow
        key={virtualRow.key}
        {...keyboardProps}
        ref={virtualizer.measureElement}
        dataIndex={virtualRow.index}
        className={cx({ [styles.lastRow]: isLast })}
        virtualized
      >
        {parentCells}
      </TableBodyRow>
    );
  };

  return (
    <>
      {topGap > 0 && <div style={{ display: "table-row", height: topGap }} aria-hidden="true" />}
      {virtualizedItems.map(renderRow)}
      {bottomGap > 0 && (
        <div style={{ display: "table-row", height: bottomGap }} aria-hidden="true" />
      )}
    </>
  );
};

export default SpaceDetailsExpandableStickyRows;
